import DataChoice, {IDataChoices} from "../../model-factory/basic/data/DataChoice";
import {IAnyObject} from "../../common/SimpleInterface";
import DataModel from "../../model-factory/basic/data/DataModel";
import DataFactory from "../../model-factory/basic/data/DataFactory";
import ApiRequestVO from "../../manager/api/ApiRequestVO";
import {FactoryID} from "../../model-factory/type-interface/ModelFactoryType";
import {IPopulateIDs} from "../../model-factory/type-interface/ModelFactoryInterface";
import ApiError from "../../manager/api/ApiError";
import {isArray, isHTML, isNativeObject, nl2br, requireDate, ucFirst} from "../../common/SiteHelper";
import {PostType} from "../../manager/post/PostManager";
import {IPostDataChoices, OrganisationType} from "./PostDataFactory";
import {getManager} from "../../manager/Manager";
import {IRouteChoices} from "../../model-factory/basic/route/RouteChoice";
import {getModelFactory} from "../../model-factory/ModelFactory";

export type SourceActivityType =
    "my-organisation" |
    "requests";

type ActivityType =
    "apply" |
    "respond" |
    "edit";

type ActivityEditType =
    "publish" |
    "pending" |
    "edit" |
    "delete" |
    "expired";

export class AwardedToVO
{
    activityID:number;
    organisationID:number;
    organisationTitle:number;

    constructor(object:IAnyObject)
    {
        this.activityID = parseInt(object.activity_id ?? "0");
        this.organisationID = parseInt(object.organisation_id ?? "0");
        this.organisationTitle = object.organisation_title ?? "";
    }
}

export class PostActivityDataChoice extends DataChoice
{

    subjectOrganisationID :number;
    subjectOrganisationTitle:string;
    subjectPostID:number;
    subjectPostType:PostType|"";
    subjectPostTitle:string;
    authorUserID:number;
    authorUserName:string;
    authorOrganisationID:number;
    authorOrganisationTitle:string;
    authorOrganisationType:OrganisationType|"";
    activityType:ActivityType|"";
    activityEditType:ActivityEditType|"";
    relatedActivityID:number;
    wasAccepted:boolean;
    wasRejected:boolean;
    furtherInformation:string;
    readableMessage:string;
    updated:string;
    created:string;
    updatedDate:Date|null;
    createdDate:Date|null;

    sourceActivityType:SourceActivityType;
    externalOrganisationID:number;
    canReply:boolean;
    clientTitle:string = "";
    clientBody:string = "";
    awardedTo:AwardedToVO[] = [];

    constructor(object:IAnyObject)
    {
        super(object);
        this.subjectOrganisationID = parseInt(object.subject_organisation_id ?? "0");
        this.subjectOrganisationTitle = object.subject_organisation_title ?? "";
        this.subjectPostID = parseInt(object.subject_post_id ?? "0");
        this.subjectPostType = object.subject_post_type ?? "";
        this.subjectPostTitle = object.subject_post_title ?? "";
        this.authorUserID = parseInt(object.author_user_id ?? "0");
        this.authorUserName = object.author_user_name ?? "";
        this.authorOrganisationID = parseInt(object.author_organisation_id ?? "0");
        this.authorOrganisationTitle = object.author_organisation_title ?? "";
        this.authorOrganisationType = object.author_organisation_type ?? "";
        this.activityType = object.activity_type ?? "";
        this.activityEditType = object.activity_edit_type ?? "";
        this.relatedActivityID = parseInt(object.related_activity_id ?? "0");
        this.wasAccepted = !!object.was_accepted;
        this.wasRejected = !!object.was_rejected;
        this.furtherInformation = object.further_information ?? "";
        this.readableMessage = object.readable_message ?? "";
        this.updated = object.updated ?? "";
        this.created = object.created ?? "";

        this.updatedDate = this.updated ? requireDate(this.updated) : null;
        this.createdDate = this.created ? requireDate(this.created) : null;

        const userOrganisationID = getManager().user.organisationID;
        const sourceOrganisationID = this.activityType === "apply" ? this.subjectOrganisationID : this.authorOrganisationID;
        let externalOrganisationID = 0;
        if(this.subjectOrganisationID && this.subjectOrganisationID !== userOrganisationID){
            if(this.subjectOrganisationID !== this.subjectPostID){
                externalOrganisationID = this.subjectOrganisationID;
            }
        } else if(this.authorOrganisationID && this.authorOrganisationID !== userOrganisationID){
            if(this.authorOrganisationID !== this.subjectPostID){
                externalOrganisationID = this.authorOrganisationID;
            }
        }
        this.sourceActivityType = sourceOrganisationID === userOrganisationID ? "my-organisation" : "requests";
        this.externalOrganisationID = externalOrganisationID;
        this.canReply = this.activityType === "apply" && !!this.authorOrganisationID && (this.authorOrganisationID !== getManager().user.organisationID);
        const awardedTo = object.awarded_to ?? [];
        if(isArray(awardedTo)){
            for(let awardedToEntry of awardedTo){
                if(isNativeObject(awardedToEntry)){
                    this.awardedTo.push( new AwardedToVO(awardedToEntry))
                }
            }
        }
    }

    getParagraphFurtherInformation(){
        let furtherInformation = this.furtherInformation.trim() ?? "";
        const furtherInformationIsHTML = furtherInformation ? isHTML(furtherInformation) : false;
        furtherInformation = furtherInformation ? nl2br(furtherInformation) : "";
        return furtherInformation ? (!furtherInformationIsHTML ? "<p>" + furtherInformation +  "</p>" : furtherInformation) : "";
    }
}

export interface IPostActivityDataChoices extends IDataChoices
{
    [key: string]: PostActivityDataChoice;
}

export class PostActivityDataModel extends DataModel
{
    choices: IPostActivityDataChoices = {};

    organisationID:number = 0;

    applyModelConditions()
    {
        this.organisationID = this.populateIDs.organisation_id ?? 0;
    }

    choiceList(choiceOrder?:string[]): PostActivityDataChoice[]
    {
        return super.choiceList(choiceOrder) as PostActivityDataChoice[];
    }

    createChoice(object: IAnyObject): PostActivityDataChoice | null
    {
        return super.createChoice(object) as PostActivityDataChoice;
    }


    getPopulateRequestVO(): ApiRequestVO|null
    {
        const requestVO = new ApiRequestVO(true);
        requestVO.endpoint = "/post/organisation/"+this.organisationID+"/activity";

        return requestVO;
    }

    populateWithRequestResponse(response: IAnyObject, replaceData:boolean): ApiError | null
    {
        let error =  super.populateWithRequestResponse(response, replaceData);
        if(error){
            return error;
        }

        let entryObjects:IAnyObject[] = response.entries && Array.isArray(response.entries) ? response.entries : [];

        this.populateChoiceList(entryObjects, replaceData);
        return null;
    }

    factory(): PostActivityDataFactory
    {
        return super.factory() as PostActivityDataFactory;
    }
}


export default class PostActivityDataFactory extends DataFactory
{

    id: FactoryID = "post_activity_data";

    birth(id: string): PostActivityDataModel
    {
        return new PostActivityDataModel(id);
    }


    getClientTitle(choice:PostActivityDataChoice):string|null
    {
        if(!choice.subjectPostType){
            return null;
        }
        let activityName = "";
        if(choice.activityType === "edit"){

            if(choice.activityEditType === "publish")
            {
                activityName = "Published";
            } else if(choice.activityEditType === "edit")
            {
                activityName = "Edited";
            } else if(choice.activityEditType === "delete")
            {
                activityName = "Removed";
            } else if(choice.activityEditType === "expired")
            {
                activityName = "Applications Closed";
            }
        }
        if(choice.activityType === "respond"){

            if(!choice.wasAccepted){
                return null;
            }
            activityName = "Awarded";
        }

        if(choice.activityType === "apply"){
            activityName = "Application Received";
        }
        if(!activityName){
            return null;
        }
        const postTypeConfig = getManager().post.config[choice.subjectPostType];
        return postTypeConfig.name + " " +activityName;
    }

    getClientBody(choice:PostActivityDataChoice, routeChoices:IRouteChoices, postDataChoices:IPostDataChoices):string|null
    {
        if(!choice.subjectPostType){
            return null;
        }
        const postTypeConfig = getManager().post.config[choice.subjectPostType];

        let subjectPostHref = choice.subjectPostID ? (routeChoices[choice.subjectPostID])?.getHref() : null;
        if(choice.subjectPostID && !subjectPostHref){
            const postStatus = postDataChoices[choice.subjectPostID]?.postStatus ?? null;
            const postTypeListRouteModel = getModelFactory().postListRoute.getDefaultModel(choice.subjectPostType, false, ((postStatus === "draft") || (postStatus === "pending")) ? postStatus : "expired");
            subjectPostHref = postTypeListRouteModel?.requireChoice(choice.subjectPostID.toString()).getHref() ?? null;
        }
        const subjectLink = subjectPostHref ? "<a href='"+subjectPostHref+"'>"+choice.subjectPostTitle+"</a>" : choice.subjectPostTitle;

        const authorOrganisationHref = choice.authorOrganisationID ? (routeChoices[choice.authorOrganisationID])?.getHref() : null;
        const authorOrganisationLink = authorOrganisationHref ? "<a href='"+authorOrganisationHref+"'>"+choice.authorOrganisationTitle+"</a>" : choice.authorOrganisationTitle;
        const lowercasePostTypeName = postTypeConfig.name.toLowerCase()
        if(choice.activityType === "edit"){
            if(choice.sourceActivityType === "my-organisation"){

                if(choice.activityEditType === "publish")
                {
                    return choice.authorUserName +" has published the "+lowercasePostTypeName+" "+subjectLink;
                }
                if(choice.activityEditType === "pending")
                {
                    return choice.authorUserName +" has sent the "+lowercasePostTypeName+" "+subjectLink +" for approval";
                }
                if(choice.activityEditType === "edit")
                {
                    return choice.authorUserName +" has edited details of the "+lowercasePostTypeName+" "+subjectLink;
                }
                if(choice.activityEditType === "delete")
                {
                    return choice.authorUserName +" has deleted the "+lowercasePostTypeName+" "+choice.subjectPostTitle;
                }
                if(choice.activityEditType === "expired")
                {
                    if(choice.authorUserName){
                        return choice.authorUserName +" has closed the "+lowercasePostTypeName+" "+subjectLink;
                    }
                    return "The "+lowercasePostTypeName+" " + subjectLink +  " is now closed";
                }
                return null;
            }
            if(choice.activityEditType === "publish")
            {
                return authorOrganisationLink +" have published the "+lowercasePostTypeName+" "+subjectLink;
            }
            if(choice.activityEditType === "pending")
            {
                return authorOrganisationLink +" have sent the "+lowercasePostTypeName+" "+subjectLink +" for approval";
            }
            if(choice.activityEditType === "edit")
            {
                return authorOrganisationLink +" have edited the "+lowercasePostTypeName+" "+subjectLink;
            }
            if(choice.activityEditType === "delete")
            {
                return authorOrganisationLink +" have removed the "+lowercasePostTypeName+" " + choice.subjectPostTitle;

            }
            if(choice.activityEditType === "expired")
            {
                return "Applications for the "+lowercasePostTypeName+" " + subjectLink + " have concluded. This "+lowercasePostTypeName+" has now been archived.";

            }

        }
        if(choice.activityType === "respond"){
            if(!choice.wasAccepted){
                return null;
            }
            let myAwardedToVO:AwardedToVO|null = null;
            let foundOrganisationIDs:number[] = [];
            const myOrganisationID = getManager().user.organisationID;
            const fromAwardedTo = choice.awardedTo.slice()
            for(let i = 0; i < fromAwardedTo.length; i ++){
                const awardedToEntry = fromAwardedTo[i];
                const awardedOrganisationRoute = awardedToEntry.organisationID ? (routeChoices[awardedToEntry.organisationID]) : null;
                if(!awardedOrganisationRoute || foundOrganisationIDs.includes(awardedToEntry.organisationID)){
                    fromAwardedTo.splice(i, 1);
                    i --;
                    continue;
                }
                foundOrganisationIDs.push(awardedToEntry.organisationID);
                if(awardedToEntry.organisationID === myOrganisationID){
                    myAwardedToVO = awardedToEntry;
                    fromAwardedTo.splice(i, 1);
                    i --;
                }
            }
            if(myAwardedToVO){
                fromAwardedTo.unshift(myAwardedToVO);
            }
            let awardedTo = "";
            for(let i = 0; i < fromAwardedTo.length; i ++){
                const awardedToEntry = fromAwardedTo[i];
                const awardedToOrganisationHref = awardedToEntry.organisationID ? (routeChoices[awardedToEntry.organisationID])?.getHref() : null;
                const awardedToOrganisationLink = awardedToOrganisationHref ? "<a href='"+awardedToOrganisationHref+"'>"+awardedToEntry.organisationTitle+"</a>" : awardedToEntry.organisationTitle;
                if(i === 2){
                    if(fromAwardedTo.length > 3){
                        awardedTo += " and others"
                    } else {
                        awardedTo += " and "+awardedToOrganisationLink;
                    }
                    break;
                }
                if(i === 1){
                    if(fromAwardedTo.length > 2){
                        awardedTo += ", "
                    } else {
                        awardedTo += " and  "
                    }
                    awardedTo += awardedToOrganisationLink;
                    continue;
                }
                awardedTo += awardedToOrganisationLink;
            }
            awardedTo = awardedTo ? " to " + awardedTo : "";
            return authorOrganisationLink +" have awarded the "+lowercasePostTypeName+" "+subjectLink+awardedTo+".";
        }
        if(choice.activityType === "apply"){
            if(choice.sourceActivityType === "my-organisation"){
                return authorOrganisationLink +" have registered an interest in the "+lowercasePostTypeName+" "+subjectLink;
            }
            const subjectOrganisationHref = choice.subjectOrganisationID ? (routeChoices[choice.subjectOrganisationID])?.getHref() : null;
            const subjectOrganisationLink = subjectOrganisationHref ? "<a href='"+subjectOrganisationHref+"'>"+choice.subjectOrganisationTitle+"</a>" : choice.subjectOrganisationTitle;
            return choice.authorUserName+" has registered an interest in the "+lowercasePostTypeName+" "+ subjectLink +" with "+subjectOrganisationLink;
        }
        return null;
    }

    postActivityDataModel(organisationID:number): PostActivityDataModel
    {
        const populateIDs:IPopulateIDs = {
            organisation_id: organisationID
        }
        return this.requireModel(populateIDs) as PostActivityDataModel
    }

    birthDataChoice(object:IAnyObject): PostActivityDataChoice
    {
        return new PostActivityDataChoice(object);
    }
}